<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import {
  useEndpoint,
  useStringFunctions,
  useOpportunities,
} from '@apparatix/composables';
import type { Opportunity, Swimlane } from '@apparatix/types/Leads';
import type { MenuItem } from 'primevue/menuitem';
import LeadLabelChip from '@apparatix/leads/LeadLabelChip.vue';
import AccountLabelTag from '@apparatix/leads/AccountLabelTag.vue';
import BlockableMenuItem from '@apparatix/leads/BlockableMenuItem.vue';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore.ts';
import { useRouter } from 'vue-router';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import SplitButton from 'primevue/splitbutton';
import type { DocumentIdAndName, DocumentIdAndNameResponse } from '@apparatix/types/Documents';

type Props = Opportunity & { validSendToLanes: Swimlane[] };

const props = defineProps<Props>();

const store = useOpportunityStore();
const { addActivityComponent, showModal } = useCreateActivity();

const { formatAsCurrency } = useStringFunctions();
const {
  buildAddActivity,
  buildAddReminder,
  buildConvertToOpportunity,
  buildMoveToInactiveMenuItem,
  buildProposalItem,
  createProposal,
  findOpportunityById,
  getProposalSplitItems,
  isOpportunityActive,
  isOpportunityCompleted,
  isProposalBlockedByProjection,
  openProposal,
  proposalProjectionWarning,
} = useOpportunities();

const router = useRouter();

const emit = defineEmits<{
  (e: 'update:change-lane', newLane: number, oldLane: number, leadId: number): void;
}>();

const el = ref();
const actionsMenuRef = ref();

const isDraggable = computed(() => {
  const opportunity = findOpportunityById(props.type, props.id);
  return !isOpportunityCompleted(opportunity);
});

const opportunity = ref({ ...props });

// Prefer the live store row so the projection gate reflects edits made in the detail flyout.
const liveOpportunity = computed(() => findOpportunityById(props.type, props.id) ?? opportunity.value);

const isCreateProposalBlocked = computed(
  () => isProposalBlockedByProjection(liveOpportunity.value?.type, liveOpportunity.value ?? {}),
);

const createProposalWarning = computed(
  () => proposalProjectionWarning(liveOpportunity.value?.type, liveOpportunity.value ?? {}),
);

const currentLane = computed(() => store.swimlanes[props.type].find(lane => lane.id === props.swimlaneId));

const onDragStart = (e: DragEvent) => {
  e.dataTransfer.dropEffect = 'move';
  e.dataTransfer.setData('text/javascript', JSON.stringify(props));
};

const toggleActionsMenu = async (e: MouseEvent) => {
  isLoading.value = true;
  e.preventDefault();
  e.stopPropagation();
  actionsMenuRef.value.toggle(e);
  if (props && !isLead()) {
    docsEndpoint.setEndpointUrl(`/crm/opportunities/${props.id}/active-document-ids`);
    await docsEndpoint.makeRequest();
    isLoading.value = false;
  }
};

const sendToAnotherLane = (laneId: number) => {
  emit('update:change-lane', laneId, props.swimlaneId, props.id);
};

const onClick = () => {
  store.selectedCampaignId[props.type] = props.id;
  router.push({ path: `/crm/${props.type === 'Lead' ? 'leads' : 'opportunities'}/detail/${props.type.toLowerCase()}/${props.id}` });
};

const isLoading = ref(false);

const documents = ref<DocumentIdAndName[]>([]);

const docsEndpoint = useEndpoint<DocumentIdAndNameResponse>({
  type: 'GET',
  successField: 'data',
  startInLoadingState: true,
  onSuccess: response => {
    documents.value = response.data.documents;
  },
  toast: { showOnSuccess: false },
});

const getOpportunityId = () => props.id;

const actionsButtonOptions = computed<MenuItem[]>(() => {
  if (!isOpportunityActive(props.type, props)) return [];

  return [
    {
      label: 'Send to...',
      items: props.validSendToLanes.map(lane => ({
        label: lane.status,
        icon: Number(lane.order) < Number(currentLane.value?.order) ? 'pi pi-arrow-left' : 'pi pi-arrow-right',
        command: () => sendToAnotherLane(lane.id),
      })),
    },
    {
      label: 'More',
      items: getItems(),
    },
  ];
});

const isLead = () => props.type === 'Lead';

const getViewType = () => isLead() ? 'leads' : 'opportunities';

const getItems = () => {
  const list = [];
  if (isLead()) {
    list.push(buildConvertToOpportunity(props.id, props.type));
  }

  const isActiveStatus = store.options[props.type].possibleStatuses.find(lane => lane.status === props.status)?.state === 'active';
  if (props.type !== 'Lead' && isActiveStatus) {
    list.push(buildProposalItem(documents.value));
  }

  list.push(
    buildMoveToInactiveMenuItem(props.type, getOpportunityId),
    buildAddReminder(getViewType(), props, showModal),
    buildAddActivity(getViewType(), props, showModal),
  );

  return list;
};
</script>
<template>
  <div
    ref="el"
    class="flex flex-col p-3 gap-2 mb-3 rounded-lg border border-[#CBD4E1] bg-white"
    :class="{'highlight': props.justAddedToUi, 'apx-won-border': props.status === 'Won'}"
    style="box-shadow: 0 1px 2px 0 #E5E5E5;"
    :draggable="isDraggable"
    @click="onClick"
    @dragstart="onDragStart"
  >
    <div class="flex justify-between items-center">
      <LeadLabelChip view="swimlanes" :temperature="props.temperature" />
      <Button
        icon="pi pi-ellipsis-v"
        severity="secondary"
        size="small"
        :disabled="!actionsButtonOptions.length"
        outlined
        :pt="{
          root: {
            style: {
              padding: 0,
              width: '20px',
              height: '20px',
              fontSize: '.5rem',
              border: 'none'
            }
          }
        }"
        @click="toggleActionsMenu"
      />
      <Menu
        ref="actionsMenuRef"
        :model="actionsButtonOptions"
        popup
      >
        <template #item="{ item, props: slotProps }">
          <div v-if="item.id === 'openProposal'" @click.stop>
            <FormSkeleton v-if="isLoading" :rows="1" :compact="true" />
            <SplitButton
              v-else
              label="Open Proposal"
              icon="pi pi-pen-to-square"
              :model="getProposalSplitItems(documents ?? [], liveOpportunity)"
              button-class="text-primary bg-primary/10 hover:bg-primary/20"
              append-to="body"
              @click="openProposal(documents[0].id, opportunity)"
              @dropdown-click.stop
            >
              <template #item="{ item: splitItem, props: splitItemProps }">
                <BlockableMenuItem :item="splitItem" :action-props="splitItemProps.action" />
              </template>
            </SplitButton>
          </div>
          <div v-else-if="item.id === 'createProposal'">
            <FormSkeleton v-if="isLoading" :rows="1" :compact="true" />
            <!-- Wrapper div keeps the tooltip hoverable while the button is disabled. -->
            <div v-else v-tooltip.left="createProposalWarning">
              <Button
                label="Create Proposal"
                icon="pi pi-pen-to-square"
                button-class="text-primary bg-primary/10 hover:bg-primary/20"
                class="w-full"
                :disabled="isCreateProposalBlocked"
                @click="createProposal(liveOpportunity)"
              />
            </div>
          </div>
          <!-- 2) Fall back to the default link layout for all others -->
          <template v-else>
            <a
              class="flex items-center gap-2 px-3 py-2 hover:bg-surface-200 rounded"
              v-bind="slotProps.action"
            >
              <span :class="item.icon" />
              <span>{{ item.label }}</span>
            </a>
          </template>
        </template>
      </Menu>
    </div>
    <p class="font-medium text-sm text-[var(--swimlane-primary-text)]">
      {{ props.name }}
    </p>
    <div class="flex flex-col items-start gap-1">
      <p class="text-xs text-[var(--swimlane-secondary-text)]">
        {{ props.company }} • {{ formatAsCurrency(props.value, false) }}
      </p>
      <AccountLabelTag :label="props.accountLabel" />
    </div>
  </div>
  <component :is="addActivityComponent" />
</template>
<style scoped>
.thumbnail {
  width: 20px;
  height: 20px;
}

@keyframes glow {
  0% {
    /* box-shadow: 0 0 5px var(--warning-background), 0 0 10px var(--warning-background), 0 0 15px var(--warning-background), 0 0 20px var(--warning-background); */
    box-shadow: inset 0px 0px 0px 0px #ffdf12;
  }
  50% {
    box-shadow: inset 0px 0px 10px 0px #ffdf12;
  }
  100% {
    box-shadow: inset 0px 0px 15px 0px #ffdf12;
  }
}

.highlight {
  animation: glow 5s infinite alternate;
}
</style>
